import {
  Button,
  buttonVariants,
} from "@arcadeai/design-system/components/ui/atoms/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@arcadeai/design-system/components/ui/atoms/dropdown-menu";
import { Switch } from "@arcadeai/design-system/components/ui/atoms/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { Link } from "@tanstack/react-router";
import { FileText, MoreVertical, Pencil, Trash2, Wrench } from "lucide-react";
import { useState } from "react";
import { useProjectPermissions } from "@/app/capabilities/api/hooks";
import { CanProject } from "@/app/capabilities/components/can-project";
import { PROJECT } from "@/app/capabilities/permissions";
import {
  useGetDeploymentStatus,
  useServerAuthStatus,
  useUpdateServer,
} from "@/app/servers/api/hooks";
import { OAuthActionButton } from "@/app/servers/components/oauth/oauth-action-button";
import { OAuthAuthorizationSheet } from "@/app/servers/components/oauth/oauth-authorization-sheet";
import { ServerSheet } from "@/app/servers/components/server-sheet";
import { requiresOAuth, type Server } from "@/app/servers/types";
import { DeleteServerButton } from "./delete-button";

export const ServerActions = ({ server }: { server: Server }) => {
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const isStatic = server?.binding?.type === "static";
  const needsOAuth = requiresOAuth(server);
  const { isAuthorized } = useServerAuthStatus(
    server.id,
    server.binding.type,
    server.type
  );
  const showAuthButton = needsOAuth && !isAuthorized;
  const { canProject } = useProjectPermissions();
  // Logs live on the server detail's logs tab, which requires deployments_read;
  // gate the list shortcut on the same permission so it never lands on a view
  // with no logs UI.
  const shouldShowLogs = server.managed && canProject(PROJECT.deployments.read);
  const canUpdateServer = canProject(PROJECT.servers.update);
  const canDeleteServer = canProject(PROJECT.servers.delete);
  const canManageServer = canUpdateServer || canDeleteServer;
  const isManaged = server.managed;

  const { mutate: updateServer, isPending: isUpdating } = useUpdateServer({
    id: server.id,
  });

  const { data: deploymentStatusData } = useGetDeploymentStatus({
    id: server.id,
    enabled: isManaged,
    activeInterval: 2000,
    stableInterval: 30_000,
  });

  const shouldShowTools =
    !isManaged || deploymentStatusData?.status === "running";

  const handleToggleEnabled = async (checked: boolean) => {
    await updateServer({
      enabled: checked,
      http: server.http
        ? {
            uri: server.http.uri,
            secret: server.http.secret.value ?? "",
            timeout: server.http.timeout,
            retry: server.http.retry,
          }
        : undefined,
      mcp: server.mcp
        ? {
            uri: server.mcp.uri,
            timeout: server.mcp.timeout,
            retry: server.mcp.retry,
          }
        : undefined,
    });
  };

  return (
    <div className="flex items-center gap-3">
      {!isStatic && (
        <CanProject permission={PROJECT.servers.update}>
          <TooltipProvider delay={200}>
            <Tooltip>
              <TooltipTrigger
                render={
                  <span
                    aria-busy={isUpdating}
                    className="mr-1 inline-flex items-center"
                  />
                }
              >
                <Switch
                  checked={server.enabled}
                  loading={isUpdating}
                  onCheckedChange={handleToggleEnabled}
                />
              </TooltipTrigger>
              <TooltipContent>
                <p>{`${server.enabled ? "Disable" : "Enable"} server`}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CanProject>
      )}
      {!!showAuthButton && (
        <OAuthAuthorizationSheet server={server}>
          <OAuthActionButton />
        </OAuthAuthorizationSheet>
      )}
      {!!shouldShowTools && (
        <Link
          className={buttonVariants({
            variant: "secondary",
            size: "sm",
            className: "gap-3",
          })}
          from="/orgs/$organizationId/projects/$projectId/servers/$serverId"
          params={{ serverId: server.id }}
          search={{ section: "tools" }}
          to="."
        >
          <Wrench className="size-4" />
          <span className="hidden md:block">Tools</span>
        </Link>
      )}
      {!!shouldShowLogs && (
        <Link
          className={buttonVariants({
            variant: "default",
            size: "sm",
            className: "gap-3",
          })}
          from="/orgs/$organizationId/projects/$projectId/servers/$serverId"
          params={{ serverId: server.id }}
          search={{ section: "logs" }}
          to="."
        >
          <FileText className="size-4" />
          <span className="hidden md:block">Logs</span>
        </Link>
      )}
      {!isStatic && canManageServer && (
        <>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger
              render={
                <Button className="h-8 w-8 p-0" size="sm" variant="ghost" />
              }
            >
              <MoreVertical className="size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="flex flex-col gap-1">
              <CanProject permission={PROJECT.servers.update}>
                <DropdownMenuItem onClick={() => setEditOpen(true)}>
                  <Pencil className="size-4" />
                  <span>Edit</span>
                </DropdownMenuItem>
              </CanProject>
              <CanProject permission={PROJECT.servers.delete}>
                <DropdownMenuItem
                  onClick={() => setDeleteOpen(true)}
                  variant="destructive"
                >
                  <Trash2 className="size-4" />
                  <span>Delete Server</span>
                </DropdownMenuItem>
              </CanProject>
            </DropdownMenuContent>
          </DropdownMenu>
          <ServerSheet
            onOpenChange={setEditOpen}
            open={editOpen}
            server={server}
          />
          <DeleteServerButton
            id={server.id}
            name={server.id}
            onOpenChange={setDeleteOpen}
            open={deleteOpen}
            showTrigger={false}
          />
        </>
      )}
    </div>
  );
};
